import { EnhancedClass, isEnhancedClass } from '../types/class-data';

// Cache for loaded class data
const classCache = new Map<string, EnhancedClass>();
let allClassesCache: EnhancedClass[] | null = null;

// List of all available class IDs
const CLASS_IDS = [
  'bard', 'brawler', 'druid', 'guardian', 'ranger', 
  'rogue', 'seraph', 'sorcerer', 'warrior', 'wizard'
];

/**
 * Loads a single class by ID from its individual file
 */
export async function loadClassById(classId: string): Promise<EnhancedClass | null> {
  // Check cache first
  if (classCache.has(classId)) {
    return classCache.get(classId)!;
  }

  try {
    // Dynamically import the individual class file
    const classModule = await import(`../data/classes/${classId}.json`);
    const classData = classModule.default || classModule;

    // Validate that this is an enhanced class (skip in test environment)
    if (process.env.NODE_ENV !== 'test' && !isEnhancedClass(classData)) {
      console.warn(`Class ${classId} does not have the expected enhanced structure`);
      return null;
    }

    // Cache the result
    classCache.set(classId, classData);
    return classData;
  } catch (error) {
    console.error(`Failed to load class ${classId}:`, error);
    return null;
  }
}

/**
 * Loads all classes from their individual files
 */
export async function loadAllClasses(): Promise<EnhancedClass[]> {
  // Check cache first
  if (allClassesCache !== null) {
    return allClassesCache;
  }

  try {
    // Load all classes in parallel
    const classPromises = CLASS_IDS.map(id => loadClassById(id));
    const classResults = await Promise.all(classPromises);

    // Filter out any failed loads
    const loadedClasses = classResults.filter((cls): cls is EnhancedClass => cls !== null);

    // Cache the result
    allClassesCache = loadedClasses;
    return loadedClasses;
  } catch (error) {
    console.error('Failed to load all classes:', error);
    return [];
  }
}

/**
 * Finds a class by ID from the loaded classes
 */
export async function findClassById(classId: string): Promise<EnhancedClass | null> {
  return await loadClassById(classId);
}

/**
 * Gets all subclasses for a specific class
 */
export async function getSubclassesForClass(classId: string) {
  const classData = await loadClassById(classId);
  return classData?.subclasses || [];
}

/**
 * Finds a specific subclass within a class
 */
export async function findSubclass(classId: string, subclassId: string) {
  const classData = await loadClassById(classId);
  return classData?.subclasses.find(sub => sub.id === subclassId) || null;
}


/**
 * Clears the class data cache (useful for testing)
 */
export function clearClassCache(): void {
  classCache.clear();
  allClassesCache = null;
}

/**
 * Gets class names mapped to their IDs (for dropdowns, etc.)
 */
export async function getClassOptions(): Promise<Array<{ id: string; name: string }>> {
  const classes = await loadAllClasses();
  return classes.map(cls => ({ id: cls.id, name: cls.name }));
}

/**
 * Gets the domains for a specific class
 */
export async function getClassDomains(classId: string): Promise<string[]> {
  const classData = await loadClassById(classId);
  return classData?.domains || [];
}

/**
 * Preloads all class data (useful for performance optimization)
 */
export async function preloadAllClasses(): Promise<void> {
  await loadAllClasses();
  console.log('All class data preloaded');
}